import React from 'react';
import type { PropertySummary } from '@/redux/internal-services/workspaceApiExtensions';

const STATUS_CFG: Record<string, { bg: string; text: string; label: string; bar: string }> = {
  active:   { bg: 'bg-green-50', text: 'text-green-700', label: 'Active',   bar: 'bg-green-300' },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-500',  label: 'Inactive', bar: 'bg-gray-300' },
  sold:     { bg: 'bg-blue-50',  text: 'text-blue-700',  label: 'Sold',     bar: 'bg-blue-300' },
};

interface Props {
  property: PropertySummary;
  onSelect?: (propertyId: string) => void;
  /** Opens the property detail landing directly on this engagement instead of its Engagement
   *  tab's multi-engagement picker. */
  onSelectEngagement?: (propertyId: string, engagementId: string) => void;
}

// engagement_type is a free string id (e.g. "repair_job") from the tenant's configurable
// workflow_engine.engagement_types list — the list card doesn't have the resolved label handy,
// so it title-cases the id the same way the backend already does for SAM's tool responses
// (see api-service app/services/llm/functions/parse_tools.py).
function formatEngagementType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const WorkspacePropertyCard: React.FC<Props> = ({ property, onSelect, onSelectEngagement }) => {
  const engagements = property.engagements ?? [];
  const sc = STATUS_CFG[property.status] ?? { bg: 'bg-gray-50', text: 'text-gray-500', label: property.status, bar: 'bg-gray-300' };

  const primaryContactFromList = property.contacts?.find(c => c.is_primary) ?? property.contacts?.[0] ?? null;
  const poc = property.contact_summary?.primary_poc ?? property.contact_summary?.current_owner ?? null;
  // List endpoint returns contacts:[] but populates contact_summary — fall back to poc
  const primaryContact = primaryContactFromList
    ?? (poc ? { name: poc.display_name, role: '', phone: poc.phone ?? null, email: poc.email ?? null, is_primary: true } : null);
  const totalContacts = property.contact_summary?.total_contacts ?? property.contacts?.length ?? 0;
  const extraContacts = totalContacts > 1 ? totalContacts - 1 : 0;

  return (
    <div className="relative rounded-2xl border border-[#EDEDED] bg-ui-card overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${engagements.length ? 'bg-brand-dark/40' : sc.bar}`} />
      <div onClick={() => onSelect?.(property.property_id)} className="px-2 py-1.5 ml-2 cursor-pointer">
        {/* Address row with property-type icon */}
        <div className="flex items-start gap-2 mb-2">
          {(() => {
            const pt = (property.property_type ?? '').toLowerCase();
            if (pt.includes('commercial'))
              return (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3 mt-0.5">
                  <rect x="2" y="7" width="20" height="15" rx="1"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                  <line x1="8" y1="12" x2="8" y2="12.01"/><line x1="12" y1="12" x2="12" y2="12.01"/><line x1="16" y1="12" x2="16" y2="12.01"/>
                  <line x1="8" y1="16" x2="8" y2="16.01"/><line x1="12" y1="16" x2="12" y2="16.01"/><line x1="16" y1="16" x2="16" y2="16.01"/>
                </svg>
              );
            if (pt.includes('industrial'))
              return (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3 mt-0.5">
                  <path d="M2 20h20"/><path d="M2 20V8l6-4v4l6-4v4l6-4v16"/>
                  <path d="M6 20v-4h4v4"/><path d="M14 20v-6h4v6"/>
                </svg>
              );
            return (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3 mt-0.5">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
                <polyline points="9 21 9 12 15 12 15 21"/>
              </svg>
            );
          })()}
          <h3 className="flex-1 min-w-0 text-xs leading-snug truncate">
            <span className="font-semibold text-ui-text">
              {[property.address.street_number, property.address.pre_direction, property.address.street_name, property.address.street_suffix, property.address.post_direction].filter(Boolean).join(' ')}
            </span>
            <span className="font-normal text-ui-text3">, {property.address.city}, {property.address.state} {property.address.postal_code}</span>
          </h3>
          {/* No engagement — fall back to property's own status flag */}
          {!engagements.length && (
            <span className={`flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap ${sc.bg} ${sc.text}`}>
              {sc.label}
            </span>
          )}
        </div>
        {/* Contacts row: primary contact + phone + overflow count */}
        {primaryContact && (
          <div className="flex items-center gap-1.5 mb-2">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              className="flex-shrink-0 text-brand-dark/70">
              <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
            <span className="text-[10px] text-ui-text3 truncate min-w-0">
              {primaryContact.name}{primaryContact.role ? ` (${primaryContact.role})` : ''}
            </span>
            {primaryContact.phone && (
              <a
                href={`tel:${primaryContact.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-[10px] font-medium text-ui-text3 hover:text-brand-dark transition-colors whitespace-nowrap ml-1"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="flex-shrink-0">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.05 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {primaryContact.phone}
              </a>
            )}
            {extraContacts > 0 && (
              <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-ui-hover text-ui-text3">
                +{extraContacts}
              </span>
            )}
          </div>
        )}

        {/* Every engagement on the property gets its own sub-card — nothing hidden behind a
            "+N more" hint. */}
        {engagements.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {engagements.map((eng) => (
              <div
                key={eng.engagement_id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectEngagement) onSelectEngagement(property.property_id, eng.engagement_id);
                  else onSelect?.(property.property_id);
                }}
                className="rounded-lg border border-[#EDEDED] bg-ui-subtle px-2 py-1.5 hover:border-brand-dark/30 hover:bg-ui-hover cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-medium text-ui-text leading-snug truncate flex-1">{eng.title}</p>
                  <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap bg-green-50 text-green-700">
                    {eng.stage}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-[10px] text-ui-text3 truncate hidden">{formatEngagementType(eng.engagement_type)}</p>
                  <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-ui-hover text-ui-text2">
                    {eng.current_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default WorkspacePropertyCard;
